// Implements: F-MCK-021
/**
 * The body of "Transferir la propiedad" (mockup v3 :2817-2830): who the mockup goes to, as a radio
 * list of the organization's people without the current owner, and the notice that the former
 * owner keeps editor access (default P4-3). Pure presentation: the container loads the people,
 * sends the transfer and hands the server's sentence back as `error`.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Avatar } from "../../../shared/ui/atoms/Avatar";
import { Icon } from "../../../shared/ui/atoms/Icon";
import styles from "./TransferOwnershipForm.module.css";

export interface TransferCandidate {
  readonly id: string;
  readonly name: string;
  readonly email: string;
}

export interface TransferOwnershipFormProps {
  readonly mockupName: string;
  /** Who keeps editor access afterwards (default P4-3), said for the person reading it. */
  readonly notice: string;
  readonly candidates: readonly TransferCandidate[];
  readonly selected: string | null;
  readonly onSelect: (userId: string) => void;
  readonly disabled: boolean;
  readonly error?: string | undefined;
}

export function TransferOwnershipForm({
  mockupName,
  notice,
  candidates,
  selected,
  onSelect,
  disabled,
  error,
}: TransferOwnershipFormProps): ReactElement {
  const { t } = useTranslation();
  const legendId = useId();
  const errorId = useId();

  return (
    <div>
      <p className={styles.intro}>{t("mockups.transfer.intro", { name: mockupName })}</p>
      {candidates.length === 0 ? (
        <p>{t("mockups.transfer.nobody")}</p>
      ) : (
        <div
          className={styles.list}
          role="radiogroup"
          aria-labelledby={legendId}
          aria-describedby={error === undefined ? undefined : errorId}
        >
          <span id={legendId} className={styles.legend}>
            {t("mockups.transfer.who")}
          </span>
          {candidates.map((person) => (
            <label key={person.id} className={styles.person}>
              <input
                type="radio"
                name="transfer-who"
                value={person.id}
                checked={selected === person.id}
                disabled={disabled}
                onChange={() => {
                  onSelect(person.id);
                }}
              />
              <Avatar name={person.name} />
              <span className={styles.who}>
                {person.name}
                <span className={styles.email}>{person.email}</span>
              </span>
            </label>
          ))}
        </div>
      )}
      <div className={styles.notice}>
        <Icon name="info" />
        <span>{notice}</span>
      </div>
      {error === undefined ? null : (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
